import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { supabase } from '../../lib/supabaseClient';
import { getPhotoUrl } from '../../api/attendance';
import {
  jornadasSoftys, puestosDeLaJornada,
  type JornadaSoftys, type PuestoRegistrado, type TipoVisitaSoftys,
} from '../../api/softys';
import {
  ESTADO_JORNADA_CLS, ESTADO_JORNADA_LABEL, esDeudaDeJornada, estadoDeJornadaSoftys,
} from '../../domain/softys';
import {
  CONFIG_TOALLITAS, leerConfigVisitasPuesto, type ConfigVisitasPuesto,
} from '../../domain/softysConfig';
import { coincideTodasLasPalabras, rangoDelMes } from '../../domain/tableFilters';
import {
  BotonDetalle, Dato, FILA_ABIERTA, PanelDeDetalle, Seccion, Senal,
} from '../../components/DetalleExpandible';
import TarjetasResumen, { type Indicador } from '../../components/TarjetasResumen';
import { useAuth } from '../../components/AuthProvider';
import { puedeEditar } from '../../domain/permissions';
import RegistroSoftysModal from './softys/RegistroSoftysModal';

const TZ = 'America/Lima';
const hoyLima = () => formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd');

/** Una campaña que recorre puestos. Sale de la bandera, no de una lista escrita a mano. */
interface CampanaConVisitas { id: string; etiqueta: string; config: ConfigVisitasPuesto }

/**
 * Registros de campo · **Visitas a puestos** (Softys · Toallitas Húmedas).
 *
 * EL EJE, que es lo que la hace útil: la bandeja **no lista lo que llegó, lista lo PROGRAMADO** y
 * le engancha encima lo registrado. Es el pedido del usuario —*«si mañana tenemos diez mercados,
 * tenemos que tener los espacios de los diez mercados ahí, para que se vaya completando»*— y es el
 * mismo eje que la bandeja de cierres de turno, que cambió por el mismo motivo.
 *
 * Una bandeja que solo muestra lo que entró sirve para consultar; una que muestra los huecos sirve
 * para perseguir. La diferencia se vio el 12-set con Atrapa tu Bienestar: siete tiendas cerraron el
 * día sin una sola jugada y nadie lo supo hasta cruzar la programación contra lo registrado.
 *
 * ⚠️ El DNI del comerciante **no se pinta en la tabla**, solo dentro del detalle. Es el documento
 * de un tercero que no es usuario del sistema: dejarlo en la lista convertiría «abrir la bandeja»
 * en una lectura masiva de documentos de identidad. Mismo criterio que el contacto del puestero en
 * el levantamiento y el del bodeguero en bodegas.
 */
export default function VisitasPuestoBandeja() {
  const { roles } = useAuth();
  // Mismo permiso que el resto de «Registros de campo». El servidor decide de verdad: las tres
  // RPC comprueban admin, datos o el coordinador DESIGNADO de la campana.
  const puedeCorregir = puedeEditar('canjes', roles);

  const [desde, setDesde] = useState(hoyLima);
  const [hasta, setHasta] = useState(hoyLima);
  const [campanias, setCampanias] = useState<CampanaConVisitas[]>([]);
  const [campania, setCampania] = useState('');
  const [mes, setMes] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [soloPendientes, setSoloPendientes] = useState(false);

  const [filas, setFilas] = useState<JornadaSoftys[]>([]);
  const [abierta, setAbierta] = useState<string | null>(null);
  const [detalle, setDetalle] = useState<Record<string, PuestoRegistrado[]>>({});
  const [cargando, setCargando] = useState(false);
  /** Qué modal está abierto. `null` = ninguno. */
  const [modal, setModal] = useState<
    { modo: 'editar'; registro: PuestoRegistrado; sujeto: string }
    | { modo: 'alta'; assignmentId: string; tipo: TipoVisitaSoftys; sujeto: string }
    | null>(null);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  // Las campañas que recorren puestos. Sale de la bandera `visitas_puesto`, así que encender una
  // campaña nueva es un UPDATE y no un despliegue.
  useEffect(() => {
    void (async () => {
      const { data, error: e } = await supabase
        .from('campaigns')
        .select('id, name, client, brand, visitas_puesto_config')
        .eq('visitas_puesto', true)
        .is('archived_at', null)
        .order('start_date', { ascending: false });
      if (e) { setError(e.message); return; }
      const lista = (data ?? []).map((c) => {
        const etiqueta = `${(c.client as string) ?? ''} — ${(c.name as string) ?? (c.brand as string) ?? ''}`;
        let config = CONFIG_TOALLITAS;
        try {
          config = leerConfigVisitasPuesto(c.visitas_puesto_config ?? null);
        } catch (err) {
          // La bandeja solo muestra: se sigue con la de Toallitas, pero se DICE.
          setError(`${etiqueta}: ${err instanceof Error ? err.message : 'configuración ilegible'}`);
        }
        return { id: c.id as string, etiqueta, config };
      });
      setCampanias(lista);
      setCampania((actual) => (actual === '' && lista.length > 0 ? lista[0].id : actual));
    })();
  }, []);

  /** Qué pregunta y qué premios lleva la campaña elegida (0334). */
  const config = campanias.find((c) => c.id === campania)?.config ?? CONFIG_TOALLITAS;

  const cargar = useCallback(async () => {
    if (campania === '') { setFilas([]); return; }
    setCargando(true);
    setError('');
    try {
      setFilas(await jornadasSoftys(campania, desde, hasta));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar las jornadas.');
    } finally {
      setCargando(false);
    }
  }, [campania, desde, hasta]);

  useEffect(() => { void cargar(); }, [cargar]);

  const hoy = hoyLima();

  /** El estado se calcula UNA vez y lo usan la fila, el filtro y las tarjetas: si cada uno lo
   *  dedujera por su cuenta, la casilla «solo los que faltan» y el contador podrían discrepar. */
  const conEstado = useMemo(
    () => filas.map((f) => ({ fila: f, estado: estadoDeJornadaSoftys(f, hoy) })),
    [filas, hoy],
  );

  const visibles = useMemo(() => conEstado.filter(({ fila, estado }) => {
    if (soloPendientes && !esDeudaDeJornada(estado)) return false;
    if (busqueda.trim() === '') return true;
    return coincideTodasLasPalabras(busqueda, fila.mercado, fila.persona ?? '');
  }), [conEstado, soloPendientes, busqueda]);

  const indicadores: Indicador[] = useMemo(() => {
    const total = conEstado.length;
    const conVisitas = conEstado.filter((x) => x.estado === 'con_visitas').length;
    const deuda = conEstado.filter((x) => esDeudaDeJornada(x.estado)).length;
    const puestos = conEstado.reduce(
      (s, x) => s + x.fila.visitasComerciante + x.fila.visitasVenta, 0,
    );
    const premios = conEstado.reduce((s, x) => s + x.fila.premios, 0);
    return [
      { titulo: 'Jornadas', valor: total, detalle: 'mercado y día programados' },
      { titulo: 'Con visitas', valor: conVisitas, tono: 'bueno' },
      { titulo: 'Aún sin registro', valor: deuda, detalle: 'jornadas ya pasadas', tono: 'malo' },
      { titulo: 'Puestos registrados', valor: puestos },
      { titulo: 'Premios entregados', valor: premios },
    ];
  }, [conEstado]);

  /**
   * 🔴 AL ABRIR, EL DETALLE MANDA: las cuentas de la fila se corrigen con lo que de verdad vino.
   *
   * Las cuentas de la tabla son una foto del momento en que se cargó la página; el detalle se lee
   * al abrirlo. Con la campaña corriendo eso se separa en minutos, y el 14-set produjo una fila
   * que decía **3 comerciantes** con **4 puestos adentro**. Una tabla que se contradice con su
   * propio detalle es tan inservible como una equivocada: quien la mira deja de creerle a las dos
   * cifras y termina contando a mano.
   *
   * Y NO se memoriza el detalle: antes, una vez abierto no se volvía a leer, así que cerrar y
   * abrir mostraba lo mismo de hace media hora. Es barato releer y es lo único que está fresco.
   */
  /**
   * Relee el detalle de una jornada, SIN mirar si estaba abierta.
   *
   * Existe aparte de `alternarDetalle` porque después de corregir o de cargar a mano hace falta
   * releer una fila que ya está abierta, y `alternarDetalle` la cerraría. Resolverlo llamándola
   * dos veces parece funcionar y no: la segunda llamada lee `abierta` del render anterior, así que
   * el resultado depende de cuándo React alcanzó a actualizar el estado.
   */
  const releerDetalle = useCallback(async (f: JornadaSoftys): Promise<void> => {
    if (f.assignmentId === null) return;
    const puestos = await puestosDeLaJornada(f.assignmentId);
    setDetalle((d) => ({ ...d, [f.positionId]: puestos }));
    const comerciante = puestos.filter((p) => p.tipo === 'comerciante').length;
    const venta = puestos.filter((p) => p.tipo === 'venta').length;
    const premios = puestos.filter((p) => p.premio !== null && p.premio !== '').length;
    setFilas((antes) => antes.map((x) => (x.positionId === f.positionId
      ? { ...x, visitasComerciante: comerciante, visitasVenta: venta, premios }
      : x)));
  }, []);

  async function alternarDetalle(f: JornadaSoftys): Promise<void> {
    const clave = f.positionId;
    if (abierta === clave) { setAbierta(null); return; }
    setAbierta(clave);
    if (f.assignmentId === null) return;
    try {
      setDetalle((d) => ({ ...d, [clave]: [] }));
      const puestos = await puestosDeLaJornada(f.assignmentId);
      setDetalle((d) => ({ ...d, [clave]: puestos }));

      const comerciante = puestos.filter((p) => p.tipo === 'comerciante').length;
      const venta = puestos.filter((p) => p.tipo === 'venta').length;
      const premios = puestos.filter((p) => p.premio !== null && p.premio !== '').length;
      setFilas((antes) => antes.map((x) => (x.positionId === clave
        ? { ...x, visitasComerciante: comerciante, visitasVenta: venta, premios }
        : x)));
    } catch (e) {
      setMensaje(e instanceof Error ? e.message : 'No se pudo cargar el detalle.');
    }
  }

  async function verFoto(path: string): Promise<void> {
    try {
      window.open(await getPhotoUrl(path), '_blank');
    } catch {
      setMensaje('No se pudo abrir la foto.');
    }
  }

  const hayFiltro = busqueda.trim() !== '' || soloPendientes;

  return (
    <div>
      <h2 className="mb-1 text-lg font-semibold text-gray-900">Visitas a puestos de mercado</h2>
      <p className="mb-4 max-w-3xl text-sm text-gray-600">
        Una fila por <b>mercado y día programado</b>, esté registrado o no. Los que quedaron en cero
        se ven acá: es la única forma de perseguirlos. Al abrir una fila aparece puesto por puesto,
        con lo que se preguntó en cada uno.
      </p>

      {/* Primera barra: lo que va al servidor. */}
      <div className="mb-2 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          Desde
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
            className="mt-1 block rounded border px-2 py-1.5" />
        </label>
        <label className="text-sm">
          Hasta
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
            className="mt-1 block rounded border px-2 py-1.5" />
        </label>
        <label className="text-sm">
          Campaña
          <select value={campania} onChange={(e) => setCampania(e.target.value)}
            className="mt-1 block rounded border px-2 py-1.5">
            {campanias.length === 0 && <option value="">(ninguna)</option>}
            {campanias.map((c) => <option key={c.id} value={c.id}>{c.etiqueta}</option>)}
          </select>
        </label>
        <button type="button" onClick={() => void cargar()} disabled={cargando}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm">
          {cargando ? 'Cargando…' : 'Actualizar'}
        </button>
      </div>

      {/* Segunda barra: recorta lo ya traído, sin volver al servidor. */}
      <div className="mb-4 flex flex-wrap items-end gap-3 border-t border-gray-100 pt-3">
        <label className="text-sm">
          Mes
          <input type="month" value={mes}
            onChange={(e) => {
              setMes(e.target.value);
              const r = rangoDelMes(e.target.value);
              if (r) { setDesde(r.desde); setHasta(r.hasta); }
            }}
            className="mt-1 block rounded border px-2 py-1.5" />
        </label>
        <label className="text-sm">
          Mercado o persona
          <input type="search" value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Ej.: Belén, Iquitos, Jakeline"
            className="mt-1 block w-64 rounded border px-2 py-1.5" />
        </label>
        <label className="flex items-center gap-2 pb-1.5 text-sm">
          <input type="checkbox" checked={soloPendientes}
            onChange={(e) => setSoloPendientes(e.target.checked)} />
          Solo los que faltan
        </label>
        {hayFiltro && (
          <span className="pb-1.5 text-sm text-gray-600">
            Mostrando {visibles.length} de {conEstado.length}
          </span>
        )}
      </div>

      <TarjetasResumen indicadores={indicadores} />

      {error !== '' && (
        <p role="alert" className="my-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          {error}
        </p>
      )}
      {mensaje !== '' && (
        <p role="status" className="my-3 rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          {mensaje}
        </p>
      )}

      <div className="mt-3 overflow-x-auto rounded border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
            <tr>
              <th className="px-3 py-2">Fecha</th>
              <th className="px-3 py-2">Mercado</th>
              <th className="px-3 py-2">Quién</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2 text-right">Comerciante</th>
              <th className="px-3 py-2 text-right">Ventas</th>
              <th className="px-3 py-2 text-right">Premios</th>
              <th className="px-3 py-2"><span className="sr-only">Acciones</span></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {visibles.map(({ fila, estado }) => {
              const abiertoAqui = abierta === fila.positionId;
              const puestos = detalle[fila.positionId];
              const hayRegistro = fila.visitasComerciante + fila.visitasVenta > 0;
              return (
                <Fragment key={fila.positionId}>
                  <tr className={`align-top ${abiertoAqui ? FILA_ABIERTA : ''}`}>
                    <td className="whitespace-nowrap px-3 py-2">{fila.fecha}</td>
                    <td className="px-3 py-2">{fila.mercado}</td>
                    <td className="px-3 py-2">
                      {fila.persona ?? <span className="text-gray-500">— sin asignar</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`rounded px-1.5 py-0.5 text-xs ${ESTADO_JORNADA_CLS[estado]}`}>
                        {ESTADO_JORNADA_LABEL[estado]}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {fila.visitasComerciante > 0
                        ? <b>{fila.visitasComerciante}</b>
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {fila.visitasVenta > 0
                        ? <b>{fila.visitasVenta}</b>
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {fila.premios > 0 ? fila.premios : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      {/* Antes solo se abria si HABIA registros, asi que una jornada en cero era
                          una fila muerta: se veia el problema y no habia nada que hacer con el.
                          Ahora se abre siempre que exista la asignacion, porque el panel de abajo
                          es justamente donde se carga a mano lo que el campo no llego a registrar. */}
                      {(hayRegistro || fila.assignmentId !== null) && (
                        <BotonDetalle abierto={abiertoAqui}
                          onClick={() => void alternarDetalle(fila)} />
                      )}
                    </td>
                  </tr>
                  {abiertoAqui && (hayRegistro || fila.assignmentId !== null) && (
                    <tr>
                      <td colSpan={8} className="p-0">
                        <PanelDeDetalle
                          titulo="Puestos registrados"
                          sujeto={`${fila.mercado} · ${fila.fecha}`}
                          onCerrar={() => setAbierta(null)}
                        >
                          {puestos === undefined && <p className="text-sm text-gray-600">Cargando…</p>}
                          {puestos?.length === 0 && (
                            <p className="text-sm text-gray-600">
                              <b>Aún sin registro.</b> Esta jornada está programada y no llegó
                              ningún puesto desde el celular.
                            </p>
                          )}
                          {/* Cargar a mano. Va acá arriba y no al final: en una jornada en cero es
                              lo único que se puede hacer, y al final de una lista vacía no se ve. */}
                          {puedeCorregir && fila.assignmentId !== null && (
                            <div className="flex flex-wrap gap-2">
                              {(['comerciante', 'venta'] as const).map((t) => (
                                <button key={t} type="button"
                                  onClick={() => setModal({
                                    modo: 'alta', assignmentId: fila.assignmentId as string, tipo: t,
                                    sujeto: `${fila.mercado} · ${fila.fecha}`,
                                  })}
                                  className="rounded border border-gray-300 px-3 py-1.5 text-sm">
                                  + Cargar {t === 'venta' ? 'una venta' : 'una visita'} a mano
                                </button>
                              ))}
                            </div>
                          )}
                          {puestos !== undefined && puestos.length > 0 && (
                            <Seccion titulo="Uno por uno" extra={`${puestos.length} puestos`} ancho>
                              <ul className="divide-y">
                                {puestos.map((p) => (
                                  <li key={p.id} className="py-2">
                                    <div className="flex flex-wrap items-baseline gap-2">
                                      <b className={`text-sm ${p.estado === 'anulado' ? 'text-gray-400 line-through' : ''}`}>
                                        Puesto {p.puestoNumero ?? '—'}
                                        {p.puestoNombre !== null && p.puestoNombre !== ''
                                          && ` · ${p.puestoNombre}`}
                                      </b>
                                      {/* Un registro anulado SIGUE viendose, tachado. Uno que
                                          desaparece de la pantalla es indistinguible de uno que
                                          nunca existio, y en un mes nadie puede explicarle al
                                          cliente por que cambio una cifra. */}
                                      {p.estado === 'anulado' && <Senal tono="grave">anulado</Senal>}
                                      {p.origin === 'manual' && <Senal>cargado a mano</Senal>}
                                      {!p.horaReal && <Senal tono="neutro">hora aproximada</Senal>}
                                      <Senal tono={p.tipo === 'venta' ? 'neutro' : 'aviso'}>
                                        {p.tipo === 'venta' ? 'venta' : 'comerciante'}
                                      </Senal>
                                      <span className="text-xs text-gray-500">
                                        {formatInTimeZone(new Date(p.ocurridoEn), TZ, 'HH:mm')}
                                      </span>
                                    </div>
                                    <dl className="mt-1 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                                      {p.tipo === 'comerciante' ? (
                                        <>
                                          <Dato etiqueta="¿Hizo pedido?">
                                            {p.hizoPedido === null
                                              ? '—'
                                              : p.hizoPedido
                                                ? `Sí · ${p.pedidoCantidad ?? '?'} ${p.pedidoUnidad ?? ''}`
                                                : 'No'}
                                          </Dato>
                                          <Dato etiqueta="¿Escuchó la capacitación?">
                                            {p.escuchoCapacitacion === null
                                              ? '—' : p.escuchoCapacitacion ? 'Sí' : 'No'}
                                          </Dato>
                                          {/* Solo en las campañas que lo preguntan (0334). */}
                                          {(config.preguntaMuestra || p.muestraEntregada !== null) && (
                                            <Dato etiqueta="¿Recibió la muestra?">
                                              {p.muestraEntregada === null
                                                ? '—' : p.muestraEntregada ? 'Sí' : 'No'}
                                            </Dato>
                                          )}
                                          {(config.dinamicasMayorista.length > 0 || p.dinamicaMayorista !== null)
                                            && p.hizoPedido === true && (
                                            <Dato etiqueta="Dinámica">
                                              {p.dinamicaMayorista ?? '—'}
                                            </Dato>
                                          )}
                                          {(config.preguntaBonificacion || p.bonificacionPacks !== null) && (
                                            <Dato etiqueta="Packs de bonificación">
                                              {p.bonificacionPacks ?? '—'}
                                            </Dato>
                                          )}
                                        </>
                                      ) : (
                                        <Dato etiqueta="Promociones compradas">
                                          {p.promociones ?? '—'}
                                        </Dato>
                                      )}
                                      <Dato etiqueta="Premio de la ruleta">
                                        {p.premio ?? <span className="text-gray-500">no giró</span>}
                                      </Dato>
                                      <Dato etiqueta="DNI del comerciante">
                                        {p.comercianteDni ?? '—'}
                                      </Dato>
                                    </dl>
                                    <div className="mt-1 flex flex-wrap gap-2 text-xs">
                                      {[
                                        { p: p.fotoFachada, t: 'fachada' },
                                        { p: p.fotoPedido, t: 'pedido' },
                                        { p: p.fotoSalida, t: 'salida' },
                                      ].filter((f) => f.p !== null).map((f) => (
                                        <button key={f.t} type="button"
                                          onClick={() => void verFoto(f.p as string)}
                                          className="text-blue-700 underline">
                                          foto {f.t}
                                        </button>
                                      ))}
                                      {puedeCorregir && p.estado === 'registrado' && (
                                        <button type="button"
                                          onClick={() => setModal({
                                            modo: 'editar', registro: p,
                                            sujeto: `${fila.mercado} · ${fila.fecha} · puesto ${p.puestoNumero ?? '—'}`,
                                          })}
                                          className="text-blue-700 underline">
                                          corregir o anular
                                        </button>
                                      )}
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </Seccion>
                          )}
                        </PanelDeDetalle>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Tres vacíos distintos: el que no tiene nada programado, el que el filtro dejó afuera, y
          el que todavía no eligió campaña. Un «no hay datos» a secas no dice cuál de los tres es. */}
      {!cargando && conEstado.length === 0 && campania !== '' && (
        <p className="mt-3 text-sm text-gray-600">
          No hay jornadas programadas de esta campaña entre esas fechas.
        </p>
      )}
      {!cargando && conEstado.length > 0 && visibles.length === 0 && (
        <p className="mt-3 text-sm text-gray-600">
          El filtro dejó todo afuera.{' '}
          <button type="button" className="text-blue-700 underline"
            onClick={() => { setBusqueda(''); setSoloPendientes(false); }}>
            Limpiar el filtro
          </button>
        </p>
      )}
      {campanias.length === 0 && (
        <p className="mt-3 text-sm text-gray-600">
          Ninguna campaña activa recorre puestos de mercado hoy.
        </p>
      )}

      {modal !== null && (
        <RegistroSoftysModal
          modo={modal.modo}
          tipo={modal.modo === 'editar' ? modal.registro.tipo : modal.tipo}
          registro={modal.modo === 'editar' ? modal.registro : undefined}
          assignmentId={modal.modo === 'alta' ? modal.assignmentId : undefined}
          config={config}
          sujeto={modal.sujeto}
          onCerrar={() => setModal(null)}
          onListo={(m) => {
            setModal(null);
            setMensaje(m);
            // Se recarga la LISTA y el detalle abierto. Sin lo primero, el contador de la fila
            // seguiria diciendo lo de antes y la tabla se contradiria con su propio detalle —
            // que es el defecto que ya aparecio el 14-set y costo entender.
            void cargar();
            const f = filas.find((x) => x.positionId === abierta);
            if (f !== undefined) void releerDetalle(f);
          }}
        />
      )}
    </div>
  );
}
